import React from "react";
import { type NextPage } from "next";
import { Text } from "@chakra-ui/react";
import { Page } from "components/page";
import { sectionSpacingY } from "components/section";
import { StackedLogos } from "components/logos";
import { DestinationMarquee, SecurityFeatures, TraditionalCdpVsHightouch } from "components/content";
import { CdpHero, CdpAccordion, CdpDiagram } from "components/platform/composable-cdp";
import { UseCaseTabs } from "components/platform-overview-content";
import { getLandingPageProps, getPageMetaImage, getSafeCustomer, type LandingPageProps } from "utils";
import {
  dataFlexibilityComparison,
  implementationEffortComparison,
  sourcesOfTruthComparison,
} from "components/content/traditional-cdp-vs-hightouch/data";
import { CenteredQuote } from "components/quotes";
import { AuraPlate, SolidPlate } from "ui/plates";
import { Superscript } from "ui/text";

/** Any updates to this page should be mirrored in ads pages. See the ads-cdp-template.tsx file. **/

const ComposableCdp: NextPage<LandingPageProps> = ({
  page,
  announcementBanner,
  defaultLogos,
  customers,
  destinations,
}) => {
  const weightWatchers = getSafeCustomer(customers, "weight-watchers");

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      theme="dark"
      bgColor="new.green.950"
    >
      <CdpHero eyebrow={page.eyebrow} title={page.title} description={page.description} />

      <StackedLogos customers={defaultLogos} my={0} pt={{ base: "biggest", md: "huger" }} />

      <TraditionalCdpVsHightouch
        title={
          <>
            90%
            <Superscript
              size="lg"
              href="https://digiday.com/marketing/the-vast-majority-of-marketers-are-unhappy-with-their-customer-data-platforms-but-then-again-not-many-are-sure-what-they-really-are/"
            />{" "}
            of marketers say their CDP does not do what they need,{" "}
            <Text as="span" color="new.green.500">
              so why do they keep buying them?
            </Text>
          </>
        }
        rows={[
          dataFlexibilityComparison,
          sourcesOfTruthComparison,
          implementationEffortComparison,
          {
            category: "Product feature bundles",
            traditionalCdp: {
              title: "One size fits all",
              description: "Traditional CDPs offer expensive product bundles with features you may not need.",
            },
            hightouch: {
              title: "Molded to your specific use cases",
              description:
                "Select from a complete set of products and features, but only purchase and implement the ones you need to solve exactly what you are trying to accomplish.",
            },
          },
        ]}
      />

      <UseCaseTabs title="Power any CDP use case, without the usual headaches" />

      <SolidPlate bgColor="new.green.950" spotlightColor="new.green.900" theme="dark">
        <CdpDiagram />
        <CdpAccordion />
      </SolidPlate>

      <SecurityFeatures
        title="Composable CDPs are more secure"
        description="Hightouch doesn’t store your data. Instead we simply read from your data warehouse where it stays safe and sound."
        textMaxW="540px"
        my={0}
        py={sectionSpacingY}
      />

      <AuraPlate condensed variant="green">
        <CenteredQuote
          quoteText="Hightouch gives us flexibility—one platform to manage all of our customer data use cases. Our marketing team has instant access to any data point living in our warehouse, and we can sync audiences immediately to any of our marketing applications. It just works."
          author={{ name: "David Cohen", job: "Chief Data Officer" }}
          company={{ name: "Weight Watchers", logo: weightWatchers.logoUrl }}
          cta={{ text: "Read the story", href: "/customers/weight-watchers" }}
        />
      </AuraPlate>

      <DestinationMarquee destinations={destinations} my={0} py={sectionSpacingY} />
    </Page>
  );
};

export const getStaticProps = getLandingPageProps("/platform/composable-cdp", {
  loadDefaultLogos: true,
  loadDestinations: "all",
  loadCustomers: ["weight-watchers"],
});

export default ComposableCdp;
